import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Spinner,
  Flex,
  Input,
  Button,
  InputGroup,
  InputLeftElement,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import ReferralsApi from '../../../../api/referrals';
import TopReferrersTable from './TopReferrersTable';
import ReferralSearchTable from './ReferralSearchTable';
import ReferralDetailsModal from './ReferralDetailsModal';

const ReferralModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [topReferrers, setTopReferrers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const toast = useToast();
  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();

  useEffect(() => {
    if (isOpen && activeTab === 0) {
      fetchTopReferrers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab]);

  const fetchTopReferrers = async () => {
    setLoading(true);
    try {
      const response = await ReferralsApi.getTopReferrers(50);
      if (response && response.data) {
        setTopReferrers(response.data);
      }
    } catch (error) {
      toast({
        title: 'שגיאה בטעינת נתוני הפניות',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: 'נא להזין מילת חיפוש',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setSearchLoading(true);
    try {
      const response = await ReferralsApi.searchUsersWithReferrals(searchQuery, 1, 20);
      if (response && response.data) {
        setSearchResults(response.data.users || []);
      }
    } catch (error) {
      toast({
        title: 'שגיאה בחיפוש משתמשים',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    onDetailsModalOpen();
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
    if (index === 0 && topReferrers.length === 0) {
      fetchTopReferrers();
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>
            <HStack spacing={4}>
              <Text>Referral Analytics</Text>
              <Badge colorScheme="blue" fontSize="sm">
                Top Referrers & User Search
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton left="1rem" right="auto" />
          <ModalBody pb={4}>
            <Tabs index={activeTab} onChange={handleTabChange}>
              <TabList>
                <Tab>
                  Top Referrers
                  {topReferrers.length > 0 && (
                    <Badge ml={2} colorScheme="green">
                      {topReferrers.length}
                    </Badge>
                  )}
                </Tab>
                <Tab>
                  Search Users
                  {searchResults.length > 0 && (
                    <Badge ml={2} colorScheme="blue">
                      {searchResults.length}
                    </Badge>
                  )}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      Top users by referral count
                    </Text>
                    
                    {loading ? (
                      <Flex justify="center" align="center" height="300px">
                        <Spinner size="lg" />
                      </Flex>
                    ) : (
                      <TopReferrersTable 
                        data={topReferrers} 
                        onViewDetails={handleViewDetails}
                      />
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <InputGroup maxW="400px">
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.300" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search by name, phone, or referral code..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                      </InputGroup>
                      <Button 
                        colorScheme="blue" 
                        onClick={handleSearch}
                        isLoading={searchLoading}
                        loadingText="Searching..."
                      >
                        Search
                      </Button>
                    </HStack>

                    {searchResults.length > 0 ? (
                      <ReferralSearchTable 
                        data={searchResults} 
                        onViewDetails={handleViewDetails}
                      />
                    ) : searchQuery && !searchLoading ? (
                      <Text color="gray.500" textAlign="center" py={8}>
                        No users found matching "{searchQuery}"
                      </Text>
                    ) : (
                      <Text color="gray.500" textAlign="center" py={8}>
                        Enter a search term to find users with referrals
                      </Text>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ReferralDetailsModal
        user={selectedUser}
        isOpen={isDetailsModalOpen}
        onClose={onDetailsModalClose}
      />
    </>
  );
};

export default ReferralModal;
